import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import { useSearch, useLocation } from 'wouter-preact'
import { Seite } from '../app.jsx'
import { api } from '../lib/api.js'
import { BildKarte } from '../components/BildKarte.jsx'
import { Laden } from '../components/Zustand.jsx'

/**
 * Bildmarken-Karten anlegen und nachbessern.
 *
 * Ein eigener Bildschirm statt einer Erweiterung des Texteditors: hier wird
 * nicht eine Karte bearbeitet, sondern ein ganzer Satz, und die Hauptarbeit
 * ist Zeichnen statt Tippen.
 *
 * Aufgezogen und verschoben wird mit Pointer-Ereignissen — ein einziger Pfad
 * für Finger, Stift und Maus. `touch-action: none` auf Bühne und Masken
 * verhindert, dass iOS-Safari die Geste als Scrollen deutet.
 */

const MINDESTGROESSE = 1.5 // Prozent; darunter war es ein Tippen, kein Aufziehen

/** Punkt eines Zeigerereignisses in Prozent der Bildkanten. */
function prozentAus(ereignis, element) {
  const kasten = element.getBoundingClientRect()
  if (!kasten.width || !kasten.height) return { x: 0, y: 0 }
  return {
    x: Math.min(Math.max(((ereignis.clientX - kasten.left) / kasten.width) * 100, 0), 100),
    y: Math.min(Math.max(((ereignis.clientY - kasten.top) / kasten.height) * 100, 0), 100),
  }
}

/** Zwei Eckpunkte zu einem Rechteck, egal in welche Richtung gezogen wurde. */
function rechteckAus(von, bis) {
  return {
    x: Math.min(von.x, bis.x),
    y: Math.min(von.y, bis.y),
    w: Math.abs(bis.x - von.x),
    h: Math.abs(bis.y - von.y),
  }
}

function begrenze(wert, min, max) {
  return Math.min(Math.max(wert, min), max)
}

function trifft(maske, punkt) {
  return (
    punkt.x >= maske.x
    && punkt.x <= maske.x + maske.w
    && punkt.y >= maske.y
    && punkt.y <= maske.y + maske.h
  )
}

/**
 * Oberste Maske unter einem Punkt, sonst -1.
 *
 * Die aktive Maske gewinnt bei Überlappung: wer sie gerade beschriftet, will
 * sie auch anfassen. Sonst die zuletzt gezeichnete — die liegt oben.
 */
function maskeUnter(masken, punkt, aktiv) {
  if (masken[aktiv] && trifft(masken[aktiv], punkt)) return aktiv
  for (let i = masken.length - 1; i >= 0; i -= 1) {
    if (trifft(masken[i], punkt)) return i
  }
  return -1
}

/**
 * Maske an den Griffpunkt hängen und dabei im Bild halten.
 *
 * Der Griff ist der Abstand zwischen Zeiger und linker oberer Ecke beim
 * Anfassen. Ohne ihn würde die Maske beim ersten Pixel Bewegung unter den
 * Finger springen.
 */
function verschoben(maske, punkt, griff) {
  return {
    x: begrenze(punkt.x - griff.x, 0, Math.max(0, 100 - maske.w)),
    y: begrenze(punkt.y - griff.y, 0, Math.max(0, 100 - maske.h)),
  }
}

function Maskenzeile({ maske, index, aktiv, aufAendern, aufLoeschen, aufWaehlen }) {
  return (
    <li class={`bildeditor__zeile ${aktiv ? 'bildeditor__zeile--aktiv' : ''}`}>
      <button
        type="button"
        class="bildeditor__nummer zahl"
        aria-label={`Maske ${index + 1} auswählen`}
        onClick={() => aufWaehlen(index)}
      >
        {index + 1}
      </button>

      <label class="feld bildeditor__antwort">
        <span class="visuell-versteckt">Antwort zu Maske {index + 1}</span>
        <input
          class="feld__eingabe"
          value={maske.label}
          placeholder="Antwort"
          onFocus={() => aufWaehlen(index)}
          onInput={(e) => aufAendern(index, { label: e.currentTarget.value })}
        />
      </label>

      <button
        type="button"
        class="knopf knopf--still knopf--klein"
        aria-label={`Maske ${index + 1} entfernen`}
        onClick={() => aufLoeschen(index)}
      >
        ×
      </button>
    </li>
  )
}

export function Bildkarten() {
  const suche = useSearch()
  const [, navigiere] = useLocation()
  const params = new URLSearchParams(suche)
  const bildAusAdresse = params.get('image') || ''

  const [ordner, setOrdner] = useState(params.get('folder') || '')
  const [alleOrdner, setAlleOrdner] = useState([])
  const [bild, setBild] = useState(bildAusAdresse)
  const [bildUrl, setBildUrl] = useState('')
  const [frage, setFrage] = useState('')
  const [name, setName] = useState('')
  const [masken, setMasken] = useState([])
  const [aktiv, setAktiv] = useState(0)
  const [entwurf, setEntwurf] = useState(null)
  const [vorschau, setVorschau] = useState(false)
  const [laedt, setLaedt] = useState(Boolean(bildAusAdresse))
  const [laedtHoch, setLaedtHoch] = useState(false)
  const [speichert, setSpeichert] = useState(false)
  const [meldung, setMeldung] = useState(null)
  const buehne = useRef(null)
  const zieht = useRef(null)

  useEffect(() => {
    api
      .get('/api/cards', {})
      .then((daten) => setAlleOrdner(daten.folders ?? []))
      .catch(() => setAlleOrdner([]))
  }, [])

  // Vorhandenen Satz laden. Die Karten-IDs kommen mit und gehen beim Speichern
  // zurück — daran erkennt der Server, welche Karte zu welcher Maske gehört.
  useEffect(() => {
    if (!bildAusAdresse) return
    setLaedt(true)
    api
      .get('/api/cards/io', { image: bildAusAdresse, folder: params.get('folder') || '' })
      .then((satz) => {
        setBild(satz.image)
        setBildUrl(satz.image_url)
        setFrage(satz.prompt || '')
        setMasken(
          satz.masks.map((m) => ({
            x: m.x, y: m.y, w: m.w, h: m.h,
            label: m.label || '', extra: m.extra || '', card: m.card || null,
          })),
        )
        setLaedt(false)
      })
      .catch((f) => {
        setMeldung(f.message)
        setLaedt(false)
      })
    // Nur beim Wechsel des Bildes neu laden; `params` ist bei jedem Rendern neu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bildAusAdresse])

  const hochladen = useCallback((datei) => {
    if (!datei) return
    setMeldung(null)
    setLaedtHoch(true)

    const formular = new FormData()
    formular.append('file', datei)
    fetch('/api/bilder', { method: 'POST', body: formular })
      .then(async (antwort) => {
        const rumpf = await antwort.json().catch(() => null)
        if (!antwort.ok) throw new Error(rumpf?.message || 'Das Bild wurde abgelehnt')
        return rumpf
      })
      .then((rumpf) => {
        setBild(rumpf.image)
        setBildUrl(rumpf.url)
        setMasken([])
        setAktiv(0)
        setLaedtHoch(false)
      })
      .catch((f) => {
        setMeldung(f.message)
        setLaedtHoch(false)
      })
  }, [])

  const aendern = useCallback((index, aenderung) => {
    setMasken((alt) => alt.map((m, i) => (i === index ? { ...m, ...aenderung } : m)))
  }, [])

  const loeschen = useCallback((index) => {
    setMasken((alt) => alt.filter((_, i) => i !== index))
    setAktiv((a) => (a > index ? a - 1 : Math.max(0, Math.min(a, index - 1))))
  }, [])

  /**
   * Ein Zeigerzug auf der Bühne macht zweierlei: auf freier Fläche zieht er
   * eine neue Maske auf, auf einer vorhandenen verschiebt er diese.
   *
   * Entschieden wird über eine Trefferprüfung in Prozentkoordinaten, nicht über
   * das Ereignisziel: alle Ereignisse laufen ohnehin an der Bühne auf, und so
   * bleibt es ein einziger Pfad für Finger, Stift und Maus.
   *
   * Der Preis: eine neue Maske lässt sich nicht innerhalb einer bestehenden
   * beginnen. Dafür daneben anfangen und darüberziehen.
   */
  function beiZeigerStart(ereignis) {
    if (!bildUrl || vorschau || ereignis.button > 0) return
    const flaeche = buehne.current
    if (!flaeche) return
    const punkt = prozentAus(ereignis, flaeche)
    flaeche.setPointerCapture?.(ereignis.pointerId)
    setEntwurf(null)

    const treffer = maskeUnter(masken, punkt, aktiv)
    if (treffer >= 0) {
      setAktiv(treffer)
      zieht.current = {
        art: 'verschieben',
        id: ereignis.pointerId,
        index: treffer,
        griff: { x: punkt.x - masken[treffer].x, y: punkt.y - masken[treffer].y },
      }
      return
    }

    zieht.current = { art: 'aufziehen', id: ereignis.pointerId, start: punkt }
  }

  function beiZeigerBewegung(ereignis) {
    const zug = zieht.current
    if (!zug || zug.id !== ereignis.pointerId) return
    ereignis.preventDefault()
    const punkt = prozentAus(ereignis, buehne.current)

    if (zug.art === 'verschieben') {
      // Direkt in den Masken, nicht im Entwurf: die Maske soll am Finger
      // kleben, nicht erst beim Loslassen springen.
      setMasken((alt) =>
        alt.map((m, i) => (i === zug.index ? { ...m, ...verschoben(m, punkt, zug.griff) } : m)),
      )
      return
    }

    setEntwurf(rechteckAus(zug.start, punkt))
  }

  function beiZeigerEnde(ereignis) {
    const zug = zieht.current
    if (!zug || zug.id !== ereignis.pointerId) return
    zieht.current = null
    buehne.current?.releasePointerCapture?.(ereignis.pointerId)

    // Verschieben ist schon während der Bewegung passiert. Ein Tippen ohne Zug
    // hat dabei nur ausgewählt — auch das ist ein gültiger Ausgang.
    if (zug.art === 'verschieben') return

    const rechteck = rechteckAus(zug.start, prozentAus(ereignis, buehne.current))
    setEntwurf(null)
    // Ein Tippen ohne Zug ist kein Versehen, sondern nur kein Rechteck.
    if (rechteck.w < MINDESTGROESSE || rechteck.h < MINDESTGROESSE) return

    setMasken((alt) => {
      setAktiv(alt.length)
      return [...alt, { ...rechteck, label: '', extra: '', card: null }]
    })
  }

  const speichern = useCallback(() => {
    if (!bild) {
      setMeldung('Erst ein Bild wählen.')
      return
    }
    if (!masken.length) {
      setMeldung('Mindestens eine Maske aufziehen — sonst gibt es nichts zu fragen.')
      return
    }

    setMeldung(null)
    setSpeichert(true)
    api
      .post('/api/cards/io', {
        folder: ordner,
        image: bild,
        prompt: frage.trim(),
        name: name.trim(),
        masks: masken,
      })
      .then(() => {
        setSpeichert(false)
        navigiere(`/karten${ordner ? `?prefix=${encodeURIComponent(ordner)}` : ''}`)
      })
      .catch((f) => {
        setSpeichert(false)
        setMeldung(f.message)
      })
  }, [bild, masken, ordner, frage, name, navigiere])

  if (laedt) return <Laden />

  const istNeu = !bildAusAdresse

  return (
    <Seite titel={istNeu ? 'Neue Bildkarten' : 'Bildkarten bearbeiten'}>
      {meldung ? (
        <p class="zustand zustand--fehler" role="alert">
          {meldung}
        </p>
      ) : null}

      <div class="bildeditor">
        <div class="bildeditor__formular">
          <label class="feld">
            <span class="feld__label meta">Thema</span>
            <input
              class="feld__eingabe"
              list="ordnerliste-bild"
              value={ordner}
              onInput={(e) => setOrdner(e.currentTarget.value)}
              placeholder="z. B. Anatomie"
            />
            <datalist id="ordnerliste-bild">
              {alleOrdner.map((o) => (
                <option key={o} value={o} />
              ))}
            </datalist>
          </label>

          <label class="feld">
            <span class="feld__label meta">Bild</span>
            <input
              class="feld__eingabe"
              type="file"
              accept="image/*"
              disabled={laedtHoch}
              onChange={(e) => hochladen(e.currentTarget.files?.[0])}
            />
            <span class="feld__hinweis meta">
              {laedtHoch
                ? 'Wird hochgeladen …'
                : bild
                  ? `Gewählt: ${bild.replace('_bilder/', '')}`
                  : 'PNG, JPEG, WebP, GIF oder AVIF, bis 12 MB'}
            </span>
          </label>

          <label class="feld">
            <span class="feld__label meta">Frage (optional, gilt für alle Masken)</span>
            <input
              class="feld__eingabe"
              value={frage}
              onInput={(e) => setFrage(e.currentTarget.value)}
              placeholder="z. B. Welche Struktur ist markiert?"
            />
          </label>

          {istNeu ? (
            <label class="feld">
              <span class="feld__label meta">Name der Karten (optional)</span>
              <input
                class="feld__eingabe"
                value={name}
                onInput={(e) => setName(e.currentTarget.value)}
                placeholder="z. B. Herz"
              />
            </label>
          ) : null}

          <div class="bildeditor__masken">
            <p class="feld__label meta">
              Masken · <span class="zahl">{masken.length}</span>
            </p>
            {masken.length ? (
              <ul class="bildeditor__liste">
                {masken.map((maske, i) => (
                  <Maskenzeile
                    key={maske.card ?? `neu-${i}`}
                    maske={maske}
                    index={i}
                    aktiv={i === aktiv}
                    aufAendern={aendern}
                    aufLoeschen={loeschen}
                    aufWaehlen={setAktiv}
                  />
                ))}
              </ul>
            ) : (
              <p class="meta">
                Noch keine Maske. Auf dem Bild ein Rechteck aufziehen — jede Maske wird
                eine eigene Karte mit eigenem Lernstand. Sitzt sie daneben, lässt sie
                sich anschließend verschieben.
              </p>
            )}
          </div>

          <div class="editor__aktionen">
            <button
              type="button"
              class="knopf knopf--haupt"
              disabled={speichert || laedtHoch}
              onClick={speichern}
            >
              {istNeu ? `${masken.length || ''} Karten anlegen`.trim() : 'Speichern'}
            </button>
            <a class="knopf knopf--still" href="/karten">
              Abbrechen
            </a>
          </div>
        </div>

        <div class="bildeditor__buehne-spalte">
          <div class="bildeditor__kopf">
            <p class="meta editor__vorschau-titel">
              {vorschau ? 'Vorschau der aktiven Karte' : 'Aufziehen · Maske ziehen verschiebt sie'}
            </p>
            <button
              type="button"
              class="knopf knopf--still knopf--klein"
              disabled={!masken.length}
              onClick={() => setVorschau((v) => !v)}
            >
              {vorschau ? 'Bearbeiten' : 'Vorschau'}
            </button>
          </div>

          {!bildUrl ? (
            <p class="meta bildeditor__leer">Erst ein Bild wählen, dann Masken aufziehen.</p>
          ) : vorschau ? (
            <BildKarte
              karte={{
                image_url: bildUrl,
                masks: masken,
                asked: Math.min(aktiv, Math.max(masken.length - 1, 0)),
                front: frage,
                back: masken[aktiv]?.label ?? '',
                back_extra: masken[aktiv]?.extra ?? '',
              }}
              umgedreht={false}
            />
          ) : (
            <div
              ref={buehne}
              class="bildeditor__buehne"
              onPointerDown={beiZeigerStart}
              onPointerMove={beiZeigerBewegung}
              onPointerUp={beiZeigerEnde}
              onPointerCancel={beiZeigerEnde}
            >
              <img class="bildeditor__bild" src={bildUrl} alt="" draggable={false} />

              {masken.map((maske, i) => (
                <span
                  key={maske.card ?? `neu-${i}`}
                  class={`bildeditor__maske ${i === aktiv ? 'bildeditor__maske--aktiv' : ''}`}
                  style={{
                    left: `${maske.x}%`,
                    top: `${maske.y}%`,
                    width: `${maske.w}%`,
                    height: `${maske.h}%`,
                  }}
                >
                  <span class="bildeditor__marke zahl">{i + 1}</span>
                </span>
              ))}

              {entwurf ? (
                <span
                  class="bildeditor__maske bildeditor__maske--entwurf"
                  style={{
                    left: `${entwurf.x}%`,
                    top: `${entwurf.y}%`,
                    width: `${entwurf.w}%`,
                    height: `${entwurf.h}%`,
                  }}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </Seite>
  )
}
